import { CssBaseline, ThemeProvider } from "@mui/material";
import React, { useContext, useMemo } from "react";
import { useQuery } from "react-query";
import { matchPath, useLocation } from "react-router-dom";
import { makeTheme } from "@knowfully/ui.common";
import useGlobalContext from "hooks/globalData";

type CustomThemeData = {
  faviconUrl?: string;
  logoImageUrl?: string;
  logoAlternativeText?: string;
  baseCssUrl?: string;
};

const CustomThemeContext = React.createContext<CustomThemeData>(
  {} as CustomThemeData
);

export const useCustomThemeContext = () =>
  useContext<CustomThemeData>(CustomThemeContext);

export type CustomThemeProviderProps = {
  children: React.ReactNode;
};

export const CustomThemeProvider = ({ children }: CustomThemeProviderProps) => {
  const { loginCompleteUrl, settingService } = useGlobalContext();
  const location = useLocation();
  const isLoginComplete = matchPath(
    { path: loginCompleteUrl(), caseSensitive: false, end: false },
    location.pathname
  );

  const { data, isFetching } = useQuery( 
    ["getSiteSettings"],
    () => settingService.getSiteSettings(),
    {
      staleTime: Infinity,
      enabled: !isLoginComplete,
      retry: false,
    }
  );


  const customThemeData = useMemo(
    () => ({
      faviconUrl: data?.theme?.faviconUrl,
      logoImageUrl: data?.theme?.logoUrl,
      logoAlternativeText: data?.theme?.logoAlternativeText,
      baseCssUrl: data?.theme?.baseCssUrl,
    }),
    [data?.theme]
  );

  if (isFetching) {
    return null;
  }

  const theme = makeTheme(data?.theme);

  return (
    <CustomThemeContext.Provider value={customThemeData}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </CustomThemeContext.Provider>
  );
};
